import React from "react";
import PropTypes from "prop-types";
import { useAuth0 } from "@auth0/auth0-react";
import { useHistory } from "react-router-dom";
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  TextField,
  Typography,
} from "@material-ui/core";
import {
  getAdminRequest,
  reviewRequest,
} from "services/commonUseDataSetsApi";

const AUTHORIZATION_ERRORS = ["consent_required", "login_required"];

export function requiresInteractiveAuthentication(error) {
  const code = String(error?.error || "").toLowerCase();
  const message = String(error?.message || "").toLowerCase();

  return AUTHORIZATION_ERRORS.some(
    (authorizationError) =>
      code === authorizationError ||
      message.includes(authorizationError.replace("_", " ")),
  );
}

export function getAuthenticationRedirectOptions(returnTo, error) {
  const options = {
    appState: { returnTo },
    audience: process.env.REACT_APP_AUTH0_AUDIENCE,
    scope: "openid profile email",
  };

  if (String(error?.error || "").toLowerCase() === "consent_required") {
    options.prompt = "consent";
  } else {
    options.prompt = "login";
  }

  return options;
}

export default function AdminRequestDetail({ match }) {
  const {
    getAccessTokenSilently,
    isAuthenticated,
    isLoading: isAuthenticationLoading,
    loginWithRedirect,
  } = useAuth0();
  const history = useHistory();
  const [detail, setDetail] = React.useState(null);
  const [files, setFiles] = React.useState([]);
  const [note, setNote] = React.useState("");
  const [errorMessage, setErrorMessage] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(true);
  const requestId = match.params.id;
  const returnTo = `/portal/common-use-data-sets/admin/${requestId}`;

  React.useEffect(() => {
    if (isAuthenticationLoading) return undefined;

    let isActive = true;

    async function loadRequest() {
      if (!isAuthenticated) {
        await loginWithRedirect(
          getAuthenticationRedirectOptions(returnTo, {
            error: "login_required",
          }),
        );
        return;
      }

      try {
        const { data } = await getAdminRequest(
          requestId,
          getAccessTokenSilently,
        );
        if (!isActive) return;
        setDetail(data);
        setFiles(data.request.requested_files);
        setErrorMessage("");
      } catch (error) {
        if (!isActive) return;

        if (requiresInteractiveAuthentication(error)) {
          await loginWithRedirect(
            getAuthenticationRedirectOptions(returnTo, error),
          );
          return;
        }

        setErrorMessage(
          error.response?.data?.messages?.authError ||
            error.response?.data?.messages?.databaseError ||
            "The data request could not be loaded.",
        );
      } finally {
        if (isActive) setIsLoading(false);
      }
    }

    loadRequest();

    return () => {
      isActive = false;
    };
  }, [
    getAccessTokenSilently,
    isAuthenticated,
    isAuthenticationLoading,
    loginWithRedirect,
    requestId,
    returnTo,
  ]);

  async function review(status) {
    try {
      setErrorMessage("");
      await reviewRequest(
        detail.request.id,
        { status, files, note },
        getAccessTokenSilently,
      );
      history.push("/portal/common-use-data-sets/admin");
    } catch (error) {
      if (requiresInteractiveAuthentication(error)) {
        await loginWithRedirect(
          getAuthenticationRedirectOptions(returnTo, error),
        );
        return;
      }

      setErrorMessage(
        error.response?.data?.messages?.authError ||
          error.response?.data?.messages?.databaseError ||
          "The data request review could not be saved.",
      );
    }
  }

  if (isAuthenticationLoading || isLoading) {
    return <CircularProgress aria-label="Loading data request" />;
  }

  if (errorMessage) {
    return <Typography color="error">{errorMessage}</Typography>;
  }

  if (!detail) return null;

  const request = detail.request;

  return (
    <Card>
      <CardContent>
        <Typography variant="h4">Data Request {request.id}</Typography>
        <Typography>
          {request.requester_name}, {request.email}
        </Typography>
        <Typography>{request.institution}</Typography>
        <Typography variant="h6">Abstract</Typography>
        <Typography style={{ whiteSpace: "pre-wrap" }}>
          {request.abstract}
        </Typography>
        <Typography variant="h6">Granted files</Typography>
        {detail.availableFiles.map((file) => (
          <FormControlLabel
            key={file.key}
            control={
              <Checkbox
                checked={files.includes(file.key)}
                onChange={() =>
                  setFiles((current) =>
                    current.includes(file.key)
                      ? current.filter((key) => key !== file.key)
                      : [...current, file.key],
                  )
                }
              />
            }
            label={file.name}
          />
        ))}
        <TextField
          fullWidth
          multiline
          minRows={3}
          label="Reviewer note, required for defer or decline"
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
        <Button
          color="primary"
          onClick={() => review("Approved")}
          disabled={!files.length}
        >
          Approve
        </Button>
        <Button
          onClick={() => review("Deferred")}
          disabled={!note.trim()}
        >
          Defer
        </Button>
        <Button
          color="secondary"
          onClick={() => review("Declined")}
          disabled={!note.trim()}
        >
          Decline
        </Button>
      </CardContent>
    </Card>
  );
}

AdminRequestDetail.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({ id: PropTypes.string.isRequired }).isRequired,
  }).isRequired,
};
